import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, getRouteApi } from "@tanstack/react-router";
import { ApiError, archivePrinterProfile, getPrinterProfile, updatePrinterProfile, type PrinterProfile } from "@/api/client";
import { formatDate } from "@/components/format";
import { DetailField, EmptyState, ErrorBanner, ToolbarButton, WorkspaceHeader } from "@/components/ui";
import { ippLabel, marginsLabel, vendorModel } from "@/features/printers/format";
import { ProfileForm } from "@/features/printers/ProfileForm";
import { AlignmentPanel, TestPrinterPanel } from "@/features/printers/PrinterTools";
import { profileFormFrom, toUpdateBody } from "@/features/printers/profileValues";

const detailApi = getRouteApi("/staff/printers/$printerId");

export function PrinterDetailPage() {
  const { printerId } = detailApi.useParams();
  const profile = useQuery({ queryKey: ["printer-profile", printerId], queryFn: () => getPrinterProfile(printerId) });

  // 404 is an unknown id, 422 a malformed one; both mean the link points at nothing.
  if (profile.error instanceof ApiError && [404, 422].includes(profile.error.status)) {
    return (
      <section className="space-y-5">
        <WorkspaceHeader title="Printer profile not found" detail="No printer profile matches this link." />
        <Link to="/printers" search={{}} className="text-sm font-medium text-zinc-950 underline">
          Back to printers
        </Link>
      </section>
    );
  }
  if (!profile.data) {
    return (
      <section className="space-y-5">
        <WorkspaceHeader title="Printer profile" detail={printerId} />
        <ErrorBanner error={profile.error} />
        {profile.isLoading ? <EmptyState title="Loading printer profile" detail="Fetching the profile." /> : null}
      </section>
    );
  }
  return <ProfileView profile={profile.data} />;
}

function ProfileView({ profile }: { profile: PrinterProfile }) {
  return (
    <section className="space-y-5">
      <WorkspaceHeader title={profile.name} detail={vendorModel(profile)} />
      {profile.archived_at ? <ArchivedProfile profile={profile} /> : <LiveProfile profile={profile} />}
    </section>
  );
}

function ArchivedProfile({ profile }: { profile: PrinterProfile }) {
  return (
    <div className="space-y-4 rounded-lg border border-zinc-200 bg-white p-4">
      <p className="rounded-md bg-zinc-50 px-3 py-2 text-sm text-zinc-700">
        This profile is archived ({formatDate(profile.archived_at ?? undefined)}). It can't be changed or used for new print
        runs; runs already made with it keep their settings.
      </p>
      <dl className="grid gap-3 text-sm md:grid-cols-3">
        <DetailField label="Paper size" value={profile.paper_size} />
        <DetailField label="Offset X" value={`${profile.offset_x_mm} mm`} />
        <DetailField label="Offset Y" value={`${profile.offset_y_mm} mm`} />
        <DetailField label="Margins" value={marginsLabel(profile)} />
        <DetailField label="Stock" value={profile.preprinted_stock ? "Pre-printed" : "Plain"} />
        <DetailField label="Default sub-batch size" value={profile.default_sub_batch_size} />
        <DetailField label="IPP printer address" value={profile.ipp_uri ?? "-"} mono />
        <DetailField label="Vendor specs" value={profile.vendor_specs ?? "-"} />
      </dl>
    </div>
  );
}

function LiveProfile({ profile }: { profile: PrinterProfile }) {
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState(false);
  const store = async (next: PrinterProfile) => {
    queryClient.setQueryData(["printer-profile", profile.id], next);
    await queryClient.invalidateQueries({ queryKey: ["printer-profiles"] });
  };
  const save = useMutation({
    mutationFn: (body: ReturnType<typeof toUpdateBody>) => updatePrinterProfile(profile.id, body),
    onSuccess: async (next) => {
      setSaved(true);
      await store(next);
    },
    onError: async (error) => {
      // A 409 means it was archived elsewhere; reload so the page shows that.
      if (error instanceof ApiError && error.status === 409) {
        await queryClient.invalidateQueries({ queryKey: ["printer-profile", profile.id] });
      }
    },
  });

  return (
    <>
      <div className="space-y-4 rounded-lg border border-zinc-200 bg-white p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h2 className="text-sm font-semibold">Profile</h2>
          <p className="text-xs text-zinc-500">
            IPP: {ippLabel(profile)}
            {profile.ipp_uri && !profile.ipp_allowed ? " (ops must add its host to PRINT_IPP_ALLOWED_HOSTS)" : ""}
          </p>
        </div>
        <ProfileForm
          key={profile.updated_at}
          initial={profileFormFrom(profile)}
          isUnchanged={(values) => Object.keys(toUpdateBody(values, profile)).length === 0}
          submitLabel="Save changes"
          pendingLabel="Saving..."
          pending={save.isPending}
          error={save.error}
          onEdit={() => {
            setSaved(false);
            if (save.error) save.reset();
          }}
          onSubmit={(values) => save.mutate(toUpdateBody(values, profile))}
        />
        {saved ? (
          <p role="status" className="text-sm font-medium text-emerald-700">
            Saved.
          </p>
        ) : null}
      </div>
      <TestPrinterPanel profile={profile} />
      <AlignmentPanel profile={profile} />
      <ArchivePanel profile={profile} onArchived={store} />
    </>
  );
}

function ArchivePanel({ profile, onArchived }: { profile: PrinterProfile; onArchived: (next: PrinterProfile) => Promise<void> }) {
  const [confirming, setConfirming] = useState(false);
  const confirmButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (confirming) confirmButton.current?.focus();
  }, [confirming]);
  const archive = useMutation({ mutationFn: () => archivePrinterProfile(profile.id), onSuccess: onArchived });

  return (
    <div className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Archive</h2>
          <p className="mt-1 text-xs text-zinc-500">Profiles are never deleted, because print runs refer to them.</p>
        </div>
        <ToolbarButton tone="danger" disabled={confirming || archive.isPending} onClick={() => setConfirming(true)}>
          Archive
        </ToolbarButton>
      </div>
      {confirming ? (
        <div role="group" aria-label="Confirm archive" className="flex flex-wrap items-center gap-3 rounded-md bg-zinc-50 p-3 text-sm">
          <p className="min-w-72 flex-1 text-zinc-700">
            Archive {profile.name}? It can no longer be changed or chosen for a print run.
          </p>
          <ToolbarButton ref={confirmButton} tone="danger" disabled={archive.isPending} onClick={() => archive.mutate()}>
            {archive.isPending ? "Archiving..." : `Archive ${profile.name}`}
          </ToolbarButton>
          <ToolbarButton disabled={archive.isPending} onClick={() => setConfirming(false)}>
            Cancel
          </ToolbarButton>
        </div>
      ) : null}
      <ErrorBanner error={archive.error} />
    </div>
  );
}
